import { Page } from "@/app/shared/components/page";
import { PageHeader } from "@groot/shell/components/layout";
import { Button } from "@groot/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@groot/ui/dialog";
import { LoadingSpinner } from "@groot/ui/loading-spinner";
import { toast } from "@/lib/toast";
import { apiClient } from "@/services/api";
import type { CreditReport } from "@/types";
import dayjs from "dayjs";
import { ArrowLeft, Download, Maximize2, TrendingUp } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

export default function CreditReportView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [creditReport, setCreditReport] = useState<CreditReport | null>(null);
  const [scoreTrend, setScoreTrend] = useState<CreditReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [isFullScreen, setIsFullScreen] = useState(false);

  useEffect(() => {
    if (id) {
      loadData(Number.parseInt(id));
    }
  }, [id]);

  const loadData = async (reportId: number) => {
    try {
      setLoading(true);
      const [reportData, trendData] = await Promise.all([
        apiClient.getCreditReport(reportId),
        apiClient.getCreditScoreTrend(),
      ]);
      setCreditReport(reportData);
      setScoreTrend(trendData);
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load credit report",
      });
      console.error(err);
      navigate("/credit-reports");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date: string) => {
    return dayjs(date).format("MMM DD, YYYY");
  };

  const formatBureau = (bureau: string) => {
    return bureau.toUpperCase();
  };

  const getBureauColor = (bureau: string) => {
    switch (bureau) {
      case "cibil":
        return "text-info";
      case "experian":
        return "text-chart-6";
      case "crif":
        return "text-success";
      default:
        return "text-muted-foreground";
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 750) return "text-success";
    if (score >= 650) return "text-warning";
    return "text-destructive";
  };

  const getScoreGrade = (score: number) => {
    if (score >= 800) return "Excellent";
    if (score >= 750) return "Very Good";
    if (score >= 700) return "Good";
    if (score >= 650) return "Fair";
    return "Poor";
  };

  // Prepare chart data
  const chartData = scoreTrend
    .filter((report) => report.creditScore !== null && report.creditScore !== undefined)
    .sort((a, b) => new Date(a.reportDate).getTime() - new Date(b.reportDate).getTime())
    .map((report) => ({
      date: dayjs(report.reportDate).format("MMM YYYY"),
      score: report.creditScore,
      bureau: formatBureau(report.bureau),
    }));

  if (loading) {
    return (
      <Page>
        <LoadingSpinner size="lg" className="py-20" />
      </Page>
    );
  }

  if (!creditReport) {
    return null;
  }

  return (
    <Page>
      {/* Header */}
      <PageHeader
        title={`${formatBureau(creditReport.bureau)} Credit Report`}
        description={formatDate(creditReport.reportDate)}
        className="mb-8"
        actions={
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => navigate("/credit-reports")}>
              <ArrowLeft className="size-4" />
              Back
            </Button>
            <Button asChild>
              <a
                href={apiClient.getCreditReportPdfUrl(creditReport.id)}
                download={creditReport.fileName}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Download className="size-4" />
                Download
              </a>
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Details and Chart Section */}
        <div className="space-y-6">
          {/* Details */}
          <div className="border border-border rounded-lg overflow-hidden">
            <div className="border-b border-border bg-muted px-6 py-4">
              <h2 className="text-base font-medium text-foreground">Report Details</h2>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <p className="text-sm text-muted-foreground">Bureau</p>
                <p className={`font-medium text-lg ${getBureauColor(creditReport.bureau)}`}>
                  {formatBureau(creditReport.bureau)}
                </p>
              </div>

              <div>
                <p className="text-sm text-muted-foreground">Report Date</p>
                <p className="font-medium">{formatDate(creditReport.reportDate)}</p>
              </div>

              <div>
                <p className="text-sm text-muted-foreground">File Name</p>
                <p className="font-medium">{creditReport.fileName}</p>
              </div>

              {creditReport.creditScore && (
                <div className="pt-4 border-t border-border">
                  <p className="text-sm text-muted-foreground mb-2">Credit Score</p>
                  <div className="flex items-baseline gap-3">
                    <p
                      className={`font-medium text-4xl ${getScoreColor(creditReport.creditScore)}`}
                    >
                      {creditReport.creditScore}
                    </p>
                    <span className="text-sm text-muted-foreground">/ 900</span>
                  </div>
                  <p className="text-sm mt-2 font-medium">
                    {getScoreGrade(creditReport.creditScore)}
                  </p>
                </div>
              )}

              {creditReport.notes && (
                <div className="pt-4 border-t border-border">
                  <p className="text-sm text-muted-foreground">Notes</p>
                  <p className="text-sm">{creditReport.notes}</p>
                </div>
              )}
            </div>
          </div>

          {/* Credit Score Trend Chart */}
          {chartData.length > 0 && (
            <div className="border border-border rounded-lg overflow-hidden">
              <div className="border-b border-border bg-muted px-6 py-4">
                <h2 className="text-base font-medium text-foreground flex items-center gap-2">
                  <TrendingUp className="size-5" />
                  Credit Score Trend
                </h2>
              </div>
              <div className="p-6">
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis domain={[300, 900]} />
                    <Tooltip
                      formatter={(value: number) => [value, "Score"]}
                      labelFormatter={(label) => `Date: ${label}`}
                    />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="score"
                      stroke="#3b82f6"
                      strokeWidth={2}
                      dot={{ fill: "#3b82f6", r: 4 }}
                      activeDot={{ r: 6 }}
                      name="Credit Score"
                    />
                  </LineChart>
                </ResponsiveContainer>
                <div className="mt-4 grid grid-cols-3 gap-2 text-center text-sm">
                  <div>
                    <p className="text-muted-foreground">Min</p>
                    <p className="font-medium">{Math.min(...chartData.map((d) => d.score || 0))}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Max</p>
                    <p className="font-medium">{Math.max(...chartData.map((d) => d.score || 0))}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Avg</p>
                    <p className="font-medium">
                      {Math.round(
                        chartData.reduce((sum, d) => sum + (d.score || 0), 0) / chartData.length,
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* PDF Viewer Section */}
        <div>
          <div className="border border-border rounded-lg overflow-hidden">
            <div className="border-b border-border bg-muted px-6 py-4 flex items-center justify-between">
              <h2 className="text-base font-medium text-foreground">PDF Document</h2>
              <Button variant="outline" size="sm" onClick={() => setIsFullScreen(true)}>
                <Maximize2 className="size-4 mr-2" />
                Full Screen
              </Button>
            </div>
            <div className="p-6">
              <div className="w-full aspect-[8.5/11] bg-muted rounded-lg overflow-hidden">
                <iframe
                  src={apiClient.getCreditReportPdfUrl(creditReport.id)}
                  className="w-full h-full border-0"
                  title="Credit Report PDF"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Full Screen PDF Dialog */}
      <Dialog open={isFullScreen} onOpenChange={setIsFullScreen}>
        <DialogContent className="max-w-[95vw] max-h-[95vh] h-[95vh] p-0 flex flex-col">
          <DialogHeader className="px-6 pt-6 pb-4 shrink-0">
            <DialogTitle>
              {formatBureau(creditReport.bureau)} - {formatDate(creditReport.reportDate)}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 px-6 pb-6 min-h-0">
            <div className="w-full h-full bg-muted rounded-lg overflow-hidden">
              <iframe
                src={apiClient.getCreditReportPdfUrl(creditReport.id)}
                className="w-full h-full border-0"
                title="Credit Report PDF - Full Screen"
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Page>
  );
}
